import { effect, inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { MainService } from "core/main.service";
import { lastValueFrom, Observable } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap } from "rxjs/operators";
import { AuthService } from "core/auth/auth.service";
import { AppLoadService } from "core/auth/app-load.service";

@Injectable({ providedIn: "root" })
export class LanguageService {
	private mainService = inject(MainService);
	private translateService = inject(TranslateService);
	private readonly auth = inject(AuthService);
	private readonly apploadService = inject(AppLoadService);

	constructor() {
		// Subscribe to changes in preferredLanguage
		this.mainService.applicationContext$
			.pipe(
				filter((context) => !!context),
				distinctUntilChanged((prev, curr) => prev.preferredLanguage === curr.preferredLanguage),
				switchMap(async (context) => this.determineAndApplyLanguageInternal("no pv needed", context.preferredLanguage)),
			)
			.subscribe({ error: (err: unknown) => console.error("Error applying language setting:", err) });
		effect(() => {
			if (!this.auth.isAuthenticated()) {
				this.determineAndApplyLanguage("_vv", "BROWSER").catch((err) =>
					console.error("Error applying language setting:", err),
				);
			}
		});
	}

	/**
	 * Returns the currently set language, even if the translations are not fetched yet.
	 */
	getCurrentLanguage(): string {
		return this.translateService.currentLang;
	}

	get langChange$(): Observable<string> {
		return this.translateService.onLangChange.pipe(map((lce) => lce.lang));
	}

	/**
	 * Sets the language, and waits until translations have been loaded.
	 *
	 */
	async setCurrentLanguage(language: string): Promise<void> {
		await lastValueFrom(this.translateService.use(language));
	}

	/**
	 * Given the language string (or "BROWSER", or "WORKSPACE"), sets the effective language.
	 */
	async determineAndApplyLanguage(processVersion: string, languageSetting: string): Promise<void> {
		return this.determineAndApplyLanguageInternal(processVersion, languageSetting);
	}

	/**
	 * Given the language string (or "BROWSER", or "WORKSPACE"), sets the effective language.
	 * processVersion is not required if application is provided.
	 */
	private async determineAndApplyLanguageInternal(processVersion: string, languageSetting: string): Promise<void> {
		if (languageSetting === "BROWSER") {
			// this needs to work also on login page
			const bootstrapProperties = this.apploadService.bootstrapProperties();
			return this.setCurrentLanguage(
				determineBrowserLanguageWithFallback(
					bootstrapProperties.browserLanguage,
					bootstrapProperties.supportedLanguages,
				),
			);
		}
		if (languageSetting === "WORKSPACE") {
			const workspaceView = await this.mainService.getCurrentWorkspace();
			return this.setCurrentLanguage(workspaceView.currentWorkspace.language);
		}
		return this.setCurrentLanguage(languageSetting);
	}
}

function determineBrowserLanguageWithFallback(browserLanguage: string, supportedLanguages: string[]): string {
	if (!supportedLanguages || supportedLanguages.length === 0 || supportedLanguages.includes(browserLanguage)) {
		return browserLanguage;
	}
	return supportedLanguages[0];
}
